import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  SkeletonCircle,
  Input,
  Stack,
  useColorModeValue,
  Avatar,
  AvatarBadge,
  IconButton,
  Center,
  Box,
  Text,
  FormErrorMessage
} from '@chakra-ui/react'
import { FaUserEdit } from 'react-icons/fa'
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "../../slices/authSlice";
import { useUpdateUserMutation } from "../../slices/userApiSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons"; 
import { useNavigate } from 'react-router-dom';
import { ToastService } from '../../services/toast/toastService';
import { useTranslation } from 'react-i18next';

const UserProfile = () => {

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [imageLoading, setImageLoading] = useState(false);

  const { success, error } = ToastService();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector( (state) => state.auth );
  const [profileImage, setProfileImage] = useState(userInfo.image_url);
  const fileInputRef = useRef(null);
  const [ updateProfile, { isLoading } ] = useUpdateUserMutation();
  const bgColor = useColorModeValue('teal.600', 'gray.800');
  const textColor = useColorModeValue('custom.800', 'custom.900');
  const navTextColor = useColorModeValue('custom.900', 'gray.200');
  const bgColorDark = useColorModeValue('gray.50', 'gray.700');
  const { t } = useTranslation();

  useEffect(() => {
    setName(userInfo.name);
    setEmail(userInfo.email);
    setPhone(userInfo.phone);
    setImageLoading(true);
  },[ userInfo.name, userInfo.email, userInfo.phone ])
  
  const validatePhone = (phone) => {
    const mmPhoneRegex = /^09\d{7,9}$/;
    if (!phone) return "";
    return mmPhoneRegex.test(phone) ? "" : t('profile.validation.invalid_phone');
  };

  const validateName = (name) => {
    if (!name || name === '') {
      return t('profile.validation.invalid_display_name');
    }
  
    if (name.length > 20) {
      return t('profile.validation.invalid_display_name_length');
    }
  
    return ""; // No error
  };

  const MAX_SIZE = 5 * 1024 * 1024;

  const submitHandler = async (e) => {
    e.preventDefault();

    const phoneError = validatePhone(phone);
    const nameError = validateName(name);

    // Validate all fields and collect errors
    const errors = {};
    if (nameError) errors.name = nameError;
    if (phoneError) errors.phone = phoneError;

    // Validate file size
    // if (selectedFile && selectedFile.size > 5 * 1024 * 1024) {
    if (selectedFile && selectedFile.size > MAX_SIZE) {
      errors.profileImage = "Profile image must be 5 MB or less.";
    }

    // If any error found, set and return
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Clear all errors
    setFormErrors({});

    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("phone", phone);
      formData.append("profileImage", selectedFile);

      if (email !== userInfo.email) {
        formData.append("email", email);
      }

      const responseFromApiCall = await updateProfile(formData).unwrap();

      dispatch(setCredentials({ ...responseFromApiCall.user }));
      success("Profile updated successfully.");
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        "An unexpected error occurred.";

      if (errorMessage.toLowerCase().includes("current password")) {
        setFormErrors({ currentPassword: errorMessage });
      } else {
        error(errorMessage);
      }
    }
  };

  const handleAvatarLoad = () => {
    setImageLoading(false);
  };

  // const handleFileChange = (e) => {
  //   const file = e.target.files[0];

  //   if (file) {
  //     const maxSizeInBytes = 5 * 1024 * 1024;

  //     if (file.size > maxSizeInBytes) {
  //       setFormErrors((prev) => ({
  //         ...prev,
  //         profileImage: "File size must be less than 5 MB",
  //       }));
  //       return;
  //     }

  //     // Clear file error
  //     setFormErrors((prev) => {
  //       const { profileImage, ...rest } = prev;
  //       return rest;
  //     });

  //     setImageLoading(true); // Show SkeletonCircle until image loads
  //     const imageUrl = URL.createObjectURL(file);
  //     setProfileImage(imageUrl);
  //     setSelectedFile(file);
  //   }
  // };

  const cropAndCompressImage = (file, maxSize = 512, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;

        canvas.width = maxSize;
        canvas.height = maxSize;

        ctx.drawImage(img, sx, sy, size, size, 0, 0, maxSize, maxSize);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Compression failed");
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              })
            );
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Size validation
    if (file.size > MAX_SIZE) {
      setFormErrors((prev) => ({
        ...prev,
        profileImage: "File size must be less than 5 MB",
      }));
      e.target.value = "";
      return;
    }

    // Type validation
    if (!file.type.startsWith("image/")) {
      setFormErrors((prev) => ({
        ...prev,
        profileImage: "Only image files are allowed",
      }));
      e.target.value = "";
      return;
    }

    try {
      setImageLoading(true);

      // Crop + compress
      const processedFile = await cropAndCompressImage(file, 512, 0.8);

      // Cleanup old preview
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }

      const previewUrl = URL.createObjectURL(processedFile);
      setProfileImage(previewUrl);
      setSelectedFile(processedFile);

      // Clear error
      setFormErrors((prev) => {
        const { profileImage, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      setFormErrors((prev) => ({
        ...prev,
        profileImage: "Image processing failed",
      }));
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value; 
  
    // Validate the input
    const errorMessage = validateName(value);
    setFormErrors(errorMessage);
  
    // Update the name state regardless of errors
    setName(value);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.trim();
    setPhone(value);

    // Clear the error only (recommended)
    setFormErrors((prev) => {
      const { profileImage, phone, ...rest } = prev;
      return rest;
    });
  };

  const handleBadgeClick = () => {
    if (fileInputRef.current) {
        fileInputRef.current.click(); // Programmatically trigger the file input
    }
  };

  return (
    <Flex
      minH='100vh' 
      direction="column"
      align={'center'}      
      bg={bgColor}
      width="100%"
      color={textColor}
      pt={`calc(1rem + env(safe-area-inset-top))`}
      >
        {/* Back Button and Header */}
          <Box display="flex" bg={bgColor} color={navTextColor} p={2} mb={4} alignItems={'center'} width="100%">
            <FontAwesomeIcon
              onClick={() => navigate('/')}
              icon={faArrowLeft}
              size="xl"
              style={{ cursor: 'pointer', marginRight: '8px' }}
            />
            <Text align="left" fontSize="xl" fontWeight="bold" ml={2}>
              {t('profile.items.profile')}
            </Text>
          </Box>
      <Stack
        w="full"
        maxW="lg"
        borderRadius="2xl"
        bg={bgColorDark}
        p={6}
        minH="calc(100vh - 100px)"
        position="relative"
        >
        <FormControl id="name">
          <Stack spacing={6}>
            <Center>
            <label htmlFor="file-upload">
              <FormControl isInvalid={!!formErrors.profileImage} align="center">
                <SkeletonCircle isLoaded={!imageLoading} size="96px">
                  <Avatar
                    key={selectedFile ? profileImage : userInfo?.image_url}
                    size="xl"
                    src={selectedFile ? profileImage : userInfo?.image_url}
                    cursor="pointer"
                    onLoad={handleAvatarLoad}
                  >
                    <AvatarBadge
                      as={IconButton}
                      bg={bgColorDark}
                      size="sm"
                      rounded="full"
                      top="-10px"
                      aria-label="Edit Image"
                      onClick={handleBadgeClick}
                      icon={<FaUserEdit />}
                    />
                  </Avatar>
                </SkeletonCircle>

                <FormErrorMessage>{formErrors.profileImage}</FormErrorMessage>
              </FormControl>

              
              {/* <SkeletonCircle isLoaded={!imageLoading} size="96px">
                {selectedFile ? (
                  <Avatar
                    key={profileImage} // forces Avatar to re-render when image changes
                    id="selectedAvatar"
                    size="xl"
                    src={profileImage}
                    cursor="pointer"
                    onLoad={handleAvatarLoad}
                  />
                ) : (
                  userInfo.image_url && (
                    <Avatar
                      key={userInfo?.image_url}
                      id="avatar"
                      size="xl"
                      src={userInfo?.image_url}
                      cursor="pointer"
                      onLoad={handleAvatarLoad}
                    >
                      <AvatarBadge
                        as={IconButton}
                        bg={bgColorDark}
                        size="sm"
                        rounded="full"
                        top="-10px"
                        aria-label="Edit Image"
                        onClick={handleBadgeClick}
                        icon={<FaUserEdit />}
                    />
                    </Avatar>
                  )
                )}
              </SkeletonCircle> */}

              <input
                id="file-upload"
                name='profileImage'
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }} // Hide the input element
              />
            </label>
            </Center>
          </Stack>
        </FormControl>
        <FormControl id="name" isRequired isInvalid={!!formErrors.name}>
          <FormLabel>{t('profile.items.name')}</FormLabel>
            <Input
              // placeholder={t('profile.validation.invalid_display_name')}
              type="text"
              value={name || ''}
              onChange={handleNameChange}
              //bg={bgColor}
              _placeholder={{ color: 'gray.500', opacity: 0.7 }} // Styled placeholder
              _focus={{
                borderColor: 'teal.500', // Focus border color
                boxShadow: '0 0 0 1px teal.500', // Focus box shadow
              }}
            />
          <FormErrorMessage>{formErrors.name}</FormErrorMessage>
        </FormControl>

        <FormControl id="email" isRequired>
          <FormLabel>{t('profile.items.email')}</FormLabel>
          <Input
            disabled
            type="email"
            value={email || ''}
            //bg={bgColor}
          />
        </FormControl>

        <FormControl id="phone" isInvalid={!!formErrors.phone}>
          <FormLabel>{t('profile.items.phone')}</FormLabel>
          <Input
            placeholder="09xxxxxxx"
            type="tel"
            value={phone || ''}
            _placeholder={{ color: 'gray.500', opacity: 0.7 }} // Styled placeholder
            onChange={handlePhoneChange}
            // onChange={(e) => setPhone(e.target.value.trim())}
          />
          <FormErrorMessage>{formErrors.phone}</FormErrorMessage>
        </FormControl>
      
        <Stack spacing={4} direction={['column', 'row']}>
          <Button
            width={'100%'}
            colorScheme="teal"
            onClick={submitHandler}
            isLoading={isLoading}
            isDisabled={imageLoading} // ✅ Disable when image is loading
          >
            {t('save')}
          </Button>
        </Stack>
      </Stack>
    </Flex>
  )
}

export default UserProfile;